import { HttpClient } from './http-client';
import { 
  ApprovalDecisionRequest,
  ApprovalResponse,
  TemporalWorkflowStatusDetail
} from '../types';

export class TemporalWorkflowService {
  constructor(private httpClient: HttpClient) {}

  /**
   * Submit an approval decision to a Temporal workflow
   */
  async submitApprovalDecision(
    divisionId: string,
    temporalWorkflowId: string,
    decision: ApprovalDecisionRequest
  ): Promise<ApprovalResponse> {
    const url = `/api/divisions/${divisionId}/workflows/temporal/${temporalWorkflowId}/approve`;
    
    try {
      const response = await this.httpClient.post<ApprovalResponse>(url, decision);
      return response;
    } catch (error) {
      console.error('Error submitting approval decision:', error);
      throw error;
    }
  }

  /**
   * Submit batch approval decisions to a Temporal workflow (processed in parallel)
   */
  async submitBatchApprovalDecisions(
    divisionId: string,
    temporalWorkflowId: string,
    decisions: ApprovalDecisionRequest[]
  ): Promise<ApprovalResponse> {
    const url = `/api/divisions/${divisionId}/workflows/temporal/${temporalWorkflowId}/approve-batch`;
    
    try {
      const response = await this.httpClient.post<ApprovalResponse>(url, { decisions });
      return response;
    } catch (error) {
      console.error('Error submitting batch approval decisions:', error);
      throw error;
    }
  }

  /**
   * Cancel a running Temporal workflow
   */
  async cancelWorkflow(
    divisionId: string,
    temporalWorkflowId: string,
    reason: string
  ): Promise<ApprovalResponse> {
    const url = `/api/v1.0/divisions/${divisionId}/workflows/temporal/${temporalWorkflowId}/cancel`;
    
    try {
      const response = await this.httpClient.post<ApprovalResponse>(url, { reason });
      return response;
    } catch (error) {
      console.error('Error cancelling workflow:', error);
      throw error;
    }
  }

  /**
   * Check whether the order has any required fields missing at its current approval step,
   * without submitting a decision. Used to prompt for required fields before opening the
   * Confirm Pickup signature capture dialog.
   */
  async checkRequiredFields(
    divisionId: string,
    temporalWorkflowId: string
  ): Promise<{ missingFields: string[] }> {
    const url = `/api/divisions/${divisionId}/workflows/temporal/${temporalWorkflowId}/required-fields-check`;

    try {
      const response = await this.httpClient.get<{ missingFields: string[] }>(url);
      return response;
    } catch (error) {
      console.error('Error checking required fields:', error);
      throw error;
    }
  }

  /**
   * Get workflow status from Temporal
   */
  async getWorkflowStatus(
    divisionId: string,
    temporalWorkflowId: string
  ): Promise<any> {
    const url = `/api/v1.0/divisions/${divisionId}/workflows/temporal/${temporalWorkflowId}/status`;
    
    try {
      const response = await this.httpClient.get(url);
      return response;
    } catch (error) {
      console.error('Error getting workflow status:', error);
      throw error;
    }
  }

  /**
   * Get rich Temporal workflow step detail for admin display.
   * Queries live Temporal state and enriches with DB approver records.
   */
  async getWorkflowDetail(
    divisionId: string,
    temporalWorkflowId: string
  ): Promise<TemporalWorkflowStatusDetail> {
    const url = `/api/divisions/${divisionId}/workflows/temporal/${temporalWorkflowId}/status`;
    try {
      const response = await this.httpClient.get<TemporalWorkflowStatusDetail>(url);
      return response;
    } catch (error) {
      console.error('Error getting Temporal workflow detail:', error);
      throw error;
    }
  }

  /**
   * Admin-only: restart (re-run) the current approval level.
   * Signals the workflow to exit the wait loop and re-execute the current level,
   * re-creating approvals and re-sending notification emails.
   */
  async adminRestartLevel(
    divisionId: string,
    temporalWorkflowId: string,
    reason: string
  ): Promise<{ message: string; temporalWorkflowId: string; restartedBy: string }> {
    const url = `/api/divisions/${divisionId}/workflows/temporal/${temporalWorkflowId}/admin-restart-level`;
    try {
      const response = await this.httpClient.post<{ message: string; temporalWorkflowId: string; restartedBy: string }>(url, { reason });
      return response;
    } catch (error) {
      console.error('Error restarting workflow level:', error);
      throw error;
    }
  }
}